import { useEffect, useRef, useState } from 'react';
import { fetchSystemStatus } from '@clarix/api-client';
import { hasInternet } from '@clarix/utils';
import { env } from '@/config/env';
import { AppSkeleton } from '@/gateway/AppSkeleton';
import { MaintenancePage } from './MaintenancePage';

interface WorkspaceUnavailableProps {
  /** Re-run the bootstrap /me query (the guard's `me.refetch`). */
  onRetry: () => void;
}

/** How often to re-probe /system/status (and retry /me) while the platform is
 *  healthy but /me is still failing. Matches MaintenancePage's own poll cadence. */
const PROBE_INTERVAL_MS = 5_000;

/**
 * Rendered by `WorkspaceShell` (the mounted workspace gate) when the bootstrap
 * `/me` call fails with a BACKEND error (503 / 5xx / CORS / network) rather than
 * a 401. We must NOT bounce to `/login` here — the session is still valid, and
 * `/login` would only send the user round in a circle while the backend is the
 * real problem.
 *
 * Instead we mirror {@link AppRoute}'s outage handling: verify with the public
 * `GET /system/status` probe and decide.
 *   - Planned maintenance, or the probe itself is unreachable while the internet
 *     works (checked with {@link hasInternet}, not `navigator.onLine`) → the
 *     platform is genuinely down → full-screen {@link MaintenancePage} (it
 *     self-polls and reloads the moment the backend recovers).
 *   - Probe healthy → the `/me` failure was a transient blip → keep the boot
 *     skeleton on screen and retry `/me` in place, leaving the user where they
 *     are until it recovers.
 *   - Probe unreachable with NO INTERNET → the user's own connection dropped, not a
 *     platform outage → also stay put and keep retrying (the OfflineBanner strip
 *     owns the connectivity message).
 */
export function WorkspaceUnavailable({ onRetry }: WorkspaceUnavailableProps) {
  const [outage, setOutage] = useState(false);
  // Keep the latest onRetry without re-running the probe loop each render.
  const onRetryRef = useRef(onRetry);
  useEffect(() => {
    onRetryRef.current = onRetry;
  });

  useEffect(() => {
    let active = true;
    let timer: number | undefined;

    const schedule = () => {
      if (active) timer = window.setTimeout(runProbe, PROBE_INTERVAL_MS);
    };

    const runProbe = () => {
      fetchSystemStatus(env.VITE_API_BASE_URL)
        .then((status) => {
          if (!active) return;
          if (status.maintenance) {
            setOutage(true); // planned window → MaintenancePage owns recovery
            return;
          }
          // Platform healthy → the /me error was transient. Retry it in place
          // and keep probing so a later full outage still escalates.
          onRetryRef.current();
          schedule();
        })
        .catch(async () => {
          // Probe unreachable. No internet is the user's own connection (stay
          // put and keep retrying); internet up means the platform is truly down.
          const online = await hasInternet();
          if (!active) return;
          if (online) setOutage(true);
          else schedule();
        });
    };

    runProbe();
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, []);

  // No onRetry passed → MaintenancePage self-polls /system/status and reloads the
  // whole boot gate the moment the platform recovers.
  if (outage) return <MaintenancePage />;

  return <AppSkeleton />;
}
